// @ts-check
import { graphql } from 'gatsby';
import React from 'react';
import { constructContentPage } from '~/components/Hocs/contentPage';
import { useContentStackProps } from '~/contentstack';
import PageModularBlocks, { PageModularBlocks_REFERENCES } from './blocks/PageModularBlocks';
import ReachOut, { ReachOut_REFERENCES } from './components/ReachOut';
import PageHero, { Head as PageHeroHead, PageHero_REFERENCES } from './hero/PageHero';

// variables (e.g. $id) are populated from the createPage call in gatsby-node.js
export const query = graphql`
	fragment AssetFragment on Ozk_assets {
		content_type
		description
		file_size
		filename
		is_dir
		title
		url
	}

	query getPage($uid: String!) {
		ozkPage(uid: { eq: $uid }) {
			uid
			_version
			page_title
			url
			include_page_title_suffix

			...PageHeroFragment

			...PageModularBlocksFragment

			...ReachOutFragment

			seo {
				meta_title
				description
				keywords
			}
		}
	}
`;

/**
 * @typedef CSPageData
 * @property {CSPage} ozkPage
 */

/**
 * @typedef SEO
 * @property {string} description
 * @property {string} keywords
 * @property {string} meta_title

 * @typedef CSPage
 * @property {string} uid
 * @property {import('./blocks/PageModularBlocks').ModularBlock[]} content
 * @property {import('./hero/PageHero').PageHeroCS[]} page_hero
 * @property {string} page_title
 * @property {import('./components/ReachOut').ReachOutCS[]} reach_out
 * @property {SEO} seo
 * @property {string} url

 * @typedef PageTemplateProps
 * @property {Location} location
 * @property {{ ozkPage: CSPage }} data
 */
/** @type {import('~/types/React').ReactFC<import('gatsby').PageProps<CSPageData>>} */
const Page = ({ location, data }) => {
	const [model, model$] = useContentStackProps({
		staticModel: data.ozkPage,
		contentType: 'page',
		contentUid: data.ozkPage.uid,
		referenceFieldPaths: [...PageHero_REFERENCES, ...PageModularBlocks_REFERENCES, ...ReachOut_REFERENCES],
	});

	return (
		<React.Fragment>
			{!!model?.page_hero?.[0] && <PageHero options={model.page_hero[0]} />}

			<PageModularBlocks options={model$?.content ?? []} />

			{!!model?.reach_out?.[0] && <ReachOut location={location} options={model.reach_out[0]} />}
		</React.Fragment>
	);
};

const cp = constructContentPage({ component: Page }, { AdditionalHeadContent: PageHeroHead });
export default cp.default;
export const Head = cp.Head;
